// @flow
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Debug from 'debug';
import Timeline from './Timeline';

const debug = Debug('amms:modules:tisPlayer:player');


type ProgressEvent = $ReadOnly<{|
    currentTime: number
|}>;

type Props = $ReadOnly<{
    playerRef: {},
    duration: number,
    interval?: number,
    className?: string,

    onStart?: () => void,
    onStop?: () => void,
    onProgress?: (e: ProgressEvent) => void,
    onTrack?: (e: ProgressEvent) => void,
    onEnd?: () => void
}>;
function Player({ playerRef, duration, interval = 100, className, onStart, onStop, onProgress, onTrack, onEnd }: Props) {
    const currentTimeRef = useRef(0);
    const previousStateRef = useRef();
    const lastCallbackTimeRef = useRef();
    const [tick, setTick] = useState(0);
    const [isPlaying, setIsPlaying] = useState();

    const play = useCallback(() => {
        debug('Play');
        setIsPlaying(true);
    }, []);
    const pause = useCallback(() => {
        debug('Pause');
        setIsPlaying(false);
    }, []);
    const stop = useCallback(() => {
        debug('Stop');
        currentTimeRef.current = 0;
        setIsPlaying(false);
    }, []);

    const _playerRef = useRef({
        isPlaying,
        play,
        pause,
        stop
    });

    useEffect(() => {
        if(playerRef) {
            if(typeof playerRef === 'function') {
                playerRef(_playerRef.current);
            }
            else {
                playerRef.current = _playerRef.current;
            }
        }
    }, [playerRef, play, pause, stop]);

    useEffect(() => {
        if(isPlaying) {
            debug('Playing');
            _playerRef.current.isPlaying = true;

            if(onStart) {
                onStart();
            }
        }
        else if(!isPlaying) {
            debug('Not playing');
            _playerRef.current.isPlaying = false;

            if(onStop) {
                onStop();
            }
        }
    }, [isPlaying, onStart, onStop]);

    useEffect(() => {
        let timerId;

        if(isPlaying) {
            timerId = setInterval(() => {
                currentTimeRef.current = currentTimeRef.current + interval;

                if(currentTimeRef.current > duration) {
                    stop();

                    if(onEnd) {
                        onEnd();
                    }
                }

                setTick({});
            }, interval);
        }

        return () => {
            clearInterval(timerId);
        };
    }, [interval, isPlaying]);

    useEffect(() => {
        if(onProgress && lastCallbackTimeRef.current !== currentTimeRef.current) {
            lastCallbackTimeRef.current = currentTimeRef.current;

            onProgress({
                interval,
                currentTime: currentTimeRef.current
            });
        }
    }, [interval, tick, onProgress]);

    const handleStartTracking = () => {
        debug('Start tracking');
        previousStateRef.current = isPlaying;
        pause();
    };

    const handleTrack = (e, newValue) => {
        currentTimeRef.current = duration * (newValue / 100);
        debug(`Track ${currentTimeRef.current}`);
        setTick({});

        if(onTrack) {
            onTrack({
                interval,
                currentTime: currentTimeRef.current
            });
        }
    };

    const handleEndTracking = useCallback(() => {
        debug(`End tracking (${previousStateRef.current ? 'resuming': 'paused'})`);

        if(previousStateRef.current) {
            play();
        }
    }, [play]);

    return (
        <div className={className}>
            {/*Player {currentTimeRef.current} / {duration}*/}
            <Timeline
                value={(currentTimeRef.current / duration) * 100}
                onMouseDown={handleStartTracking}
                onChange={handleTrack}
                onMouseUp={handleEndTracking}
            />
        </div>
    );
}

export default Player;